import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as action from './Store/SpecTemplate.action';
import { Page_Size as fixPageSize, checkArray, createOptions } from '../../../Components/helper';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import { getMultiSubBrandDetails, getPCTypesDetails, getCatBasedOnPT } from '../../SystemMasters/Store/SystemMasters.action';

export const StateContext = createContext(null);

export default function SpecContextProvider({ children }) {
  const dispatch = useDispatch();
  const reducerState = useSelector((redState) => redState);
  const {
    SpecTemplateData: { specListData, totalCount, BrandList, CategoryOneData, CategoryTwoData, CategoryThreeData, AllGradingList },
    SystemMastersDetails: { multiSubBrandList, pcTypeList },
    UserDetails: { userInfo },
    Users: { logInRights },
  } = reducerState;
  const loggedInUserId = userInfo.userId;

  const { CheckActionPermission } = useComponent();
  const SpecAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'spec', 'addEdit');

  const SpecDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'spec', 'delete');

  const initialSetting = {
    cardList: [],
    searchText: '',
    showMobileSearch: '',
    searchLoaded: false,
    archiveSection: false,
    loading: false,
    pageLoading: false,
    pageSize: fixPageSize,
    pageIndex: 1,
    hasMore: false,
    selectedIds: [],
    deleteId: null,
    showDeletePopup: false,
    showSingleDeletePopup: false,
    duplicateModal: false,
    duplicateData: null,
    expandCard: '',
    BrandData: [],
    SubBrandData: [],
    selectGradingBrand: [],
    selectSubBrand: [],
    CategoryOneData: [],
    selectCategoryOne: [],
    CategoryTwoData: [],
    selectCategoryTwo: [],
    CategoryThreeData: [],
    selectCategoryThree: [],
    isFilter: false,
    GradingList: [],
    selectGrading: [],
    filterSort: null,
    selectProductType: [],
    ProductTypeData: [],
    catBasedOnPTData: false,
  };

  const [SpecState, setSpecState] = useState(initialSetting);

  const isallChecked = checkArray(SpecState?.cardList) && SpecState?.selectedIds?.length === SpecState?.cardList?.length;

  const handleCheckAll = useCallback(
    (e) => {
      if (e.target.checked) {
        const allIds = SpecState?.cardList && checkArray(SpecState?.cardList) && SpecState?.cardList?.map((ele) => ele._id);
        if (allIds) {
          setSpecState((prevState) => ({
            ...prevState,
            selectedIds: allIds.filter((element) => element !== undefined),
          }));
        }
      } else {
        setSpecState((prevState) => ({
          ...prevState,
          selectedIds: [],
        }));
      }
    },
    [SpecState?.cardList, setSpecState],
  );

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setSpecState((prev) => ({
          ...prev,
          catBasedOnPTData: !!res,
        }));
      } catch (e) {
        setSpecState((prev) => ({
          ...prev,
          catBasedOnPTData: false,
        }));
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);
  useEffect(() => {
    dispatch(getMultiSubBrandDetails({ brandId: SpecState?.selectGradingBrand, loggedInUserId }));
  }, [dispatch, loggedInUserId, SpecState?.selectGradingBrand]);

  useEffect(() => {
    if (Array.isArray(specListData)) {
      const newIds = specListData.map((ele) => ele._id);
      setSpecState((prev) => ({
        ...prev,
        cardList: [...SpecState.cardList, ...specListData],
        selectedIds: isallChecked ? [...prev.selectedIds, ...newIds] : prev.selectedIds,
        hasMore: totalCount > specListData.length,
      }));
    }
  }, [specListData?.length, totalCount, SpecState?.filterSort]);

  useEffect(() => {
    try {
      dispatch(
        getPCTypesDetails({
          loggedInUserId: userInfo?.userId,
          isActive: true,
          tier: 'Tier1',
        }),
      );
    } catch (e) {
      // no-op
    }
  }, [dispatch, userInfo?.userId]);

  useEffect(() => {
    setSpecState((prev) => ({
      ...prev,
      BrandData: createOptions(BrandList, 'name', '_id'),
      SubBrandData: createOptions(multiSubBrandList, 'name', '_id'),
      CategoryOneData: createOptions(CategoryOneData, 'name', '_id'),
      CategoryTwoData: createOptions(CategoryTwoData, 'name', '_id'),
      GradingList: createOptions(AllGradingList, 'name', '_id'),
      ProductTypeData: createOptions(pcTypeList, 'name', '_id'),
    }));
  }, [BrandList, CategoryOneData, CategoryTwoData, AllGradingList, multiSubBrandList, pcTypeList]);

  useEffect(() => {
    setSpecState((prev) => ({
      ...prev,
      CategoryThreeData: createOptions(CategoryThreeData, 'name', '_id'),
    }));
  }, [CategoryThreeData]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      let res;
      if (SpecState.searchLoaded) {
        setSpecState((debouncestate) => ({
          ...debouncestate,
          pageIndex: 1,
          hasMore: true,
          cardList: [],
          loading: true,
        }));
        dispatch(action.clearReducer());
        const reqObj = {
          pageIndex: SpecState?.pageIndex,
          pageSize: SpecState?.pageSize,
          searchText: SpecState?.searchText,
          brand: JSON.stringify(SpecState?.selectGradingBrand),
          productType: JSON.stringify(SpecState?.selectProductType),
          subBrand: JSON.stringify(SpecState?.selectSubBrand),
          catOne: JSON.stringify(SpecState?.selectCategoryOne),
          catTwo: JSON.stringify(SpecState?.selectCategoryTwo),
          catThree: JSON.stringify(SpecState?.selectCategoryThree),
          gradingGroup: JSON.stringify(SpecState?.selectGrading),
          section: 'libraries',
          module: 'spec',
          action: 'read',
        };
        res = await dispatch(action.fetchSpecLibraries(reqObj));
      }
      if (res) {
        setSpecState((debstate) => ({
          ...debstate,
          searchLoaded: false,
          loading: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SpecState.searchText, dispatch]);

  const contextValue = useMemo(() => {
    return {
      SpecState,
      setSpecState,
      isallChecked,
      handleCheckAll,
      loggedInUserId,
      SpecAddEditAccess,
      SpecDeleteAccess,
    };
  }, [SpecState, setSpecState, isallChecked, handleCheckAll, loggedInUserId, SpecAddEditAccess, SpecDeleteAccess]);

  useEffect(() => {
    const callFn = async () => {
      const localstorageFilter = JSON.parse(localStorage.getItem('SpecListFilter'));
      let reqObj = {};
      if (
        localstorageFilter &&
        (localstorageFilter?.brandId?.length ||
          localstorageFilter?.subBrandId?.length ||
          localstorageFilter?.productTypeId?.length ||
          localstorageFilter?.catOneId?.length ||
          localstorageFilter?.catTwoId?.length ||
          localstorageFilter?.catThreeId?.length ||
          localstorageFilter?.gradingGroup?.length)
      ) {
        reqObj = {
          pageIndex: 1,
          pageSize: SpecState?.pageSize,
          loggedInUserId,
          brand: JSON.stringify(localstorageFilter?.brandId),
          subBrand: JSON.stringify(localstorageFilter?.subBrandId),
          productType: JSON.stringify(localstorageFilter?.productTypeId),
          catOne: JSON.stringify(localstorageFilter?.catOneId),
          catTwo: JSON.stringify(localstorageFilter?.catTwoId),
          catThree: JSON.stringify(localstorageFilter?.catThreeId),
          gradingGroup: JSON.stringify(localstorageFilter?.gradingGroup),
          section: 'libraries',
          module: 'spec',
          action: 'read',
        };
        setSpecState((prevState) => ({
          ...prevState,
          selectGradingBrand: localstorageFilter?.brandId,
          selectProductType: localstorageFilter?.productTypeId,
          selectSubBrand: localstorageFilter?.subBrandId,
          selectCategoryOne: localstorageFilter?.catOneId,
          selectCategoryTwo: localstorageFilter?.catTwoId,
          selectCategoryThree: localstorageFilter?.catThreeId,
          selectGrading: localstorageFilter?.gradingGroup,
          isFilter: true,
        }));
      } else {
        reqObj = {
          pageIndex: 1,
          pageSize: SpecState.pageSize,
          searchText: SpecState?.searchText,
          section: 'libraries',
          module: 'spec',
          action: 'read',
        };
      }
      setSpecState((prev) => ({
        ...prev,
        loading: true,
      }));

      const res = await dispatch(action.fetchSpecLibraries(reqObj));
      if (res) {
        setSpecState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    };

    callFn();
  }, []);

  return <StateContext.Provider value={contextValue}>{children}</StateContext.Provider>;
}

export function useSpecLibraryState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the PIMModule State Provider');
  }
  return context;
}
